import { useState, useRef, useEffect, useCallback } from "react";

export const useSlider = (
  onSliderDrag,
  onSliderKeyDown,
  onSliderDragStart,
  onSliderDragEnd,
  onFocus,
  onBlur,
  type,
  trackRef
) => {
  const [isSelected, setIsSelected] = useState(false);
  const nodeRef = useRef(null);

  const onSliderDragRef = useRef(onSliderDrag);
  const onSliderDragEndRef = useRef(onSliderDragEnd);

  useEffect(() => {
    onSliderDragRef.current = onSliderDrag;
  }, [onSliderDrag]);

  useEffect(() => {
    onSliderDragEndRef.current = onSliderDragEnd;
  }, [onSliderDragEnd]);

  const handleMouseMove = useCallback(
    event => {
      if (!trackRef.current) return;
      const clientX = event.clientX;
      const trackClientRect = trackRef.current.getBoundingClientRect();
      const position = {
        x: clientX - trackClientRect.left,
        y: 0
      };
      onSliderDragRef.current(position, type);
    },
    [type, trackRef]
  );

  const handleMouseUp = useCallback(() => {
    setIsSelected(false);
    if (nodeRef.current) {
      nodeRef.current.ownerDocument.removeEventListener(
        "mousemove",
        handleMouseMove
      );
      nodeRef.current.ownerDocument.removeEventListener(
        "mouseup",
        handleMouseUp
      );
    }
    onSliderDragEndRef.current();
  }, [handleMouseMove]);

  const handleMouseDown = useCallback(
    event => {
      event.preventDefault();
      event.currentTarget.focus();
      setIsSelected(true);
      if (nodeRef.current) {
        nodeRef.current.ownerDocument.addEventListener(
          "mousemove",
          handleMouseMove
        );
        nodeRef.current.ownerDocument.addEventListener(
          "mouseup",
          handleMouseUp
        );
      }
      onSliderDragStart();
    },
    [handleMouseMove, handleMouseUp, onSliderDragStart]
  );

  const handleTouchMove = useCallback(
    event => {
      if (event.touches && event.touches.length > 0 && trackRef.current) {
        const touch = event.touches[0];
        const trackClientRect = trackRef.current.getBoundingClientRect();
        const position = {
          x: touch.clientX - trackClientRect.left,
          y: 0
        };
        onSliderDragRef.current(position, type);
      }
    },
    [type, trackRef]
  );

  const handleTouchEnd = useCallback(() => {
    setIsSelected(false);
    if (nodeRef.current) {
      nodeRef.current.ownerDocument.removeEventListener(
        "touchmove",
        handleTouchMove
      );
      nodeRef.current.ownerDocument.removeEventListener(
        "touchend",
        handleTouchEnd
      );
    }
    onSliderDragEndRef.current();
  }, [handleTouchMove]);

  const handleTouchStart = useCallback(
    event => {
      event.preventDefault();
      event.currentTarget.focus();
      setIsSelected(true);
      if (nodeRef.current) {
        nodeRef.current.ownerDocument.addEventListener(
          "touchmove",
          handleTouchMove
        );
        nodeRef.current.ownerDocument.addEventListener(
          "touchend",
          handleTouchEnd
        );
      }
      onSliderDragStart();
    },
    [handleTouchMove, handleTouchEnd, onSliderDragStart]
  );

  const handleKeyDown = useCallback(event => onSliderKeyDown(event, type), [
    onSliderKeyDown,
    type
  ]);

  const handleBlur = useCallback(
    event => {
      setIsSelected(false);
      onBlur(event, type);
    },
    [onBlur, type]
  );

  const handleFocus = useCallback(
    event => {
      setIsSelected(true);
      onFocus(event, type);
    },
    [onFocus, type]
  );

  useEffect(
    () => () => {
      if (nodeRef.current) {
        nodeRef.current.ownerDocument.removeEventListener(
          "mousemove",
          handleMouseMove
        );
        nodeRef.current.ownerDocument.removeEventListener(
          "mouseup",
          handleMouseUp
        );
        nodeRef.current.ownerDocument.removeEventListener(
          "touchmove",
          handleTouchMove
        );
        nodeRef.current.ownerDocument.removeEventListener(
          "touchend",
          handleTouchEnd
        );
      }
    },
    [handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]
  );

  return {
    isSelected,
    nodeRef,
    handleMouseDown,
    handleTouchStart,
    handleKeyDown,
    handleFocus,
    handleBlur
  };
};
